import { useCheckoutLinkMutation } from '@/features/subscription/subscription.mutation';
import {
  useAppsAssignedToSubscriptionQuery,
  useSubscriptionsQuery,
} from '@/features/subscription/subscription.query';
import { AddTechnicalUserDialog } from '@/features/technical-user/add-technical-user-dialog';
import { ApiTokenDialog } from '@/features/technical-user/api-token-dialog';
import { TechnicalUserTable } from '@/features/technical-user/technical-user-table';
import { useTechnicalUsersQuery } from '@/features/technical-user/technical-user.query';
import { AsyncButton } from '@/shared/components/button/async-button';
import { createFileRoute } from '@tanstack/react-router';
import { CreditCardIcon, InfoIcon } from 'lucide-react';
import { useState } from 'react';
import z from 'zod';

export const Route = createFileRoute('/api-key')({
  component: RouteComponent,
  validateSearch: z.object({
    checkoutSuccessful: z.boolean().optional(),
  }),
});

function RouteComponent() {
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState('');

  const { checkoutSuccessful } = Route.useSearch();

  const {
    data: technicalUsersData,
    isLoading,
    error,
  } = useTechnicalUsersQuery({
    page,
    pageSize,
    sort: 'tokenValueCreatedAt,desc',
  });

  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
  } = useSubscriptionsQuery({
    page,
    pageSize,
  });

  const usageSubscription = subscriptionsData?.content.find(
    (subscription) =>
      subscription.type === 'Usage' && !subscription.trial,
  );
  const hasActiveUsageSubscription = usageSubscription?.active;

  const { data: appsData, isLoading: isLoadingApps } =
    useAppsAssignedToSubscriptionQuery({
      subscriptionId: usageSubscription?.id || '',
    });

  const firstApplication = appsData?.[0];

  const checkoutLinkMutation = useCheckoutLinkMutation();

  const handleOpenBillingPortal = async () => {
    if (usageSubscription?.id) {
      try {
        const result = await checkoutLinkMutation.mutateAsync({
          subscriptionId: usageSubscription.id,
        });
        if (result.checkoutUrl) {
          window.open(result.checkoutUrl);
        }
      } catch (error) {
        console.error('Failed to get billing portal link:', error);
      }
    }
  };

  const handleTokenGenerated = (token: string) => {
    setTokenValue(token);
    setTokenDialogOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl overflow-y-auto px-4 py-6">
      <div className="mt-10 mb-6 space-y-3">
        <h1 className="text-foreground text-3xl font-bold tracking-tight">
          API-Schlüssel
        </h1>
        <p className="text-muted-foreground">
          Verwalten Sie API-Schlüssel für die programmatische Nutzung
          von e2.
        </p>
      </div>

      <div className="space-y-6">
        {checkoutSuccessful === false && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <InfoIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Zahlungsvorgang nicht abgeschlossen
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    Der Zahlungsvorgang konnte nicht erfolgreich
                    abgeschlossen werden. Bitte versuchen Sie es
                    erneut oder kontaktieren Sie den Support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoadingSubscriptions && !hasActiveUsageSubscription ? (
          <div className="mb-6 rounded-md border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <InfoIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Aktives Zahlungsmittel erforderlich
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Um API-Schlüssel zu erstellen und zu verwenden,
                    benötigen Sie ein aktives Zahlungsmittel.
                  </p>
                </div>
                <div className="mt-3">
                  <AsyncButton
                    onClick={() => void handleOpenBillingPortal()}
                    disabled={!usageSubscription?.id}
                    isLoading={checkoutLinkMutation.isPending}
                  >
                    <CreditCardIcon className="size-4" />
                    Zahlungsmittel hinterlegen
                  </AsyncButton>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AddTechnicalUserDialog
            disabled={
              isLoading ||
              isLoadingApps ||
              !hasActiveUsageSubscription ||
              !firstApplication
            }
            onTokenGenerated={handleTokenGenerated}
            applicationId={firstApplication?.id}
          />
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-4">
            <p className="text-sm">
              Fehler beim Laden der API-Schlüssel:{' '}
              {error instanceof Error
                ? error.message
                : 'Unbekannter Fehler'}
            </p>
          </div>
        )}

        <TechnicalUserTable
          data={technicalUsersData}
          setPage={setPage}
          isLoading={isLoading}
          onTokenGenerated={handleTokenGenerated}
        />
      </div>

      <ApiTokenDialog
        open={tokenDialogOpen}
        onOpenChange={setTokenDialogOpen}
        tokenValue={tokenValue}
      />
    </div>
  );
}
